// Notes bridge — talks to the `web-notes` Edge Function.
//
// On Electron, notes are per-user JSON files on disk
// (<userData>/notes/<scriptKey>.json — see src/main/notes.js).
// On web, the equivalent persistence is a Postgres row scoped by
// user_id + scriptKey, served by the `web-notes` Edge Function.
// Same schema shape: { version, scriptKey, scriptNote, sceneNotes }.

const EMPTY_PAYLOAD = (scriptKey) => ({
  version: 1,
  scriptKey: scriptKey || '',
  scriptNote: null,
  sceneNotes: {},
});

function pickScriptKey(args) {
  // Same resolution priority as src/main/notes.js: cloudDraftId →
  // Lastslate-ID → sha-of-filePath (we don't compute a sha on web;
  // we use the raw filePath/cloudId as the key — keys are scoped per
  // user_id so collisions across users aren't a concern).
  if (!args) return null;
  if (args.cloudDraftId) return `draft:${args.cloudDraftId}`;
  if (args.lastslateId) return `script:${args.lastslateId.toLowerCase()}`;
  if (args.filePath) return `path:${args.filePath}`;
  return null;
}

async function callNotes({ supabase, env, action, scriptKey, payload }) {
  if (!supabase) return { ok: false, error: 'supabase_not_configured' };
  if (!env?.SUPABASE_URL) return { ok: false, error: 'supabase_url_missing' };
  const { data: sess } = await supabase.auth.getSession();
  const jwt = sess?.session?.access_token;
  if (!jwt) return { ok: false, error: 'not_signed_in' };
  const url = `${env.SUPABASE_URL.replace(/\/$/, '')}/functions/v1/web-notes`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ action, scriptKey, payload }),
    });
  } catch (err) {
    return { ok: false, error: 'network', detail: err?.message };
  }
  if (res.status === 204) return { ok: true };
  let parsed = null;
  try {
    parsed = await res.json();
  } catch {
    /* empty */
  }
  if (!res.ok || !parsed?.ok) {
    return {
      ok: false,
      error: parsed?.error || `http_${res.status}`,
      status: res.status,
    };
  }
  return parsed;
}

export function buildNotesHandlers({ supabase, env }) {
  return {
    async notesRead(args) {
      const scriptKey = pickScriptKey(args);
      if (!scriptKey) return { ok: true, payload: EMPTY_PAYLOAD(null) };
      const r = await callNotes({
        supabase,
        env,
        action: 'read',
        scriptKey,
      });
      if (!r.ok) {
        // Soft failure: hand back an empty payload so the notes pane
        // still mounts. The renderer surfaces errors via its own
        // status line.
        return { ok: true, payload: EMPTY_PAYLOAD(scriptKey), error: r.error };
      }
      return { ok: true, payload: r.payload || EMPTY_PAYLOAD(scriptKey) };
    },

    async notesWrite(args) {
      const scriptKey = pickScriptKey(args);
      if (!scriptKey) return { ok: false, error: 'no_script_key' };
      const payload = args?.payload || EMPTY_PAYLOAD(scriptKey);
      // Normalise the payload so the server-side validation has a
      // predictable shape regardless of what the renderer sent.
      const normalized = {
        version: 1,
        scriptKey,
        scriptNote: payload.scriptNote || null,
        sceneNotes: payload.sceneNotes || {},
      };
      return callNotes({
        supabase,
        env,
        action: 'write',
        scriptKey,
        payload: normalized,
      });
    },
  };
}
